import { create } from "zustand";
import {
  createEngagementLetterTemplate,
  deleteAttachment,
  getBusinessEngagementLetterTemplate,
  getEngagementLetterStatus,
  getPreviewPdf,
  listAttachments,
  reorderAttachments as reorderAttachmentsAPI,
  signEngagementLetter,
  uploadAttachment,
} from "@api/engagement";
import { useAuthStore } from "./authStore";
import type {
  EngagementLetterAttachment,
  EngagementLetterStatus,
  EngagementLetterTemplate,
} from "@/types/engagement";

interface EngagementLetterState {
  // Template management
  template: EngagementLetterTemplate | null;
  isLoadingTemplate: boolean;
  templateError: string | null;

  // Status management (keyed by tax year)
  statusByYear: Map<number, EngagementLetterStatus>;
  loadingStatusByYear: Map<number, boolean>;
  statusErrors: Map<number, string>;

  // Modal state (for clients)
  isModalOpen: boolean;
  currentYear: number | null;

  // Settings state (for tax pro)
  engagementLetterSettings: {
    content: string;
    title: string;
    loading: boolean;
  };

  // Tax pro modals
  isPreviewModalOpen: boolean;
  isPublishModalOpen: boolean;

  // Preview PDF
  previewPdfUrl: string | null;
  isLoadingPreview: boolean;
  previewError: string | null;

  // Attachments management
  attachments: EngagementLetterAttachment[];
  isLoadingAttachments: boolean;
  isUploadingAttachment: boolean;
  attachmentError: string | null;

  // Actions
  fetchTemplate: () => Promise<void>;
  fetchStatusForYear: (year: number) => Promise<void>;
  checkAndShowModal: (
    year: number,
    organizerStatus: string
  ) => Promise<boolean>;
  openModal: (year: number) => void;
  closeModal: () => void;
  signLetter: (signature: string) => Promise<void>;
  updateSettings: (title: string, content: string) => void;
  saveTemplate: () => Promise<void>;
  clearStatusForYear: (year: number) => void;

  // Tax pro modal actions
  openPreviewModal: () => void;
  closePreviewModal: () => void;
  openPublishModal: () => void;
  closePublishModal: () => void;

  // Preview actions
  fetchPreviewPdf: () => Promise<void>;

  // Attachment actions
  fetchAttachments: () => Promise<void>;
  uploadAttachmentFile: (file: File) => Promise<void>;
  deleteAttachmentById: (attachmentId: string) => Promise<void>;
  reorderAttachments: (attachmentIds: string[]) => Promise<void>;
}

export const useEngagementLetterStore = create<EngagementLetterState>(
  (set, get) => ({
    // Initial state
    template: null,
    isLoadingTemplate: false,
    templateError: null,
    statusByYear: new Map(),
    loadingStatusByYear: new Map(),
    statusErrors: new Map(),
    isModalOpen: false,
    currentYear: null,
    engagementLetterSettings: {
      content: "",
      title: "",
      loading: false,
    },
    isPreviewModalOpen: false,
    isPublishModalOpen: false,
    previewPdfUrl: null,
    isLoadingPreview: false,
    previewError: null,
    attachments: [],
    isLoadingAttachments: false,
    isUploadingAttachment: false,
    attachmentError: null,

    // Fetch template
    fetchTemplate: async () => {
      // Get businessId from user data (works for both clients and tax pros)
      const user = useAuthStore.getState().user;
      const businessId = user?.businessId;

      if (!businessId) {
        set({ templateError: "Business ID not found" });
        return;
      }

      set({ isLoadingTemplate: true, templateError: null });
      try {
        const template = await getBusinessEngagementLetterTemplate(businessId);

        set({
          template,
          isLoadingTemplate: false,
          engagementLetterSettings: {
            title: template.header,
            content: template.body,
            loading: false,
          },
        });
      } catch (error) {
        set({
          templateError: "Failed to load template",
          isLoadingTemplate: false,
        });
      }
    },

    // Fetch status for a specific year
    fetchStatusForYear: async (year: number) => {
      // Get businessId from user data (works for both clients and tax pros)
      const user = useAuthStore.getState().user;
      const businessId = user?.businessId;

      if (!businessId) {
        console.warn(
          `[EngagementLetter] Cannot fetch status for year ${year}: Business ID not found`
        );
        const errors = new Map(get().statusErrors);
        errors.set(year, "Business ID not found");
        set({ statusErrors: errors });
        return;
      }

      const loading = new Map(get().loadingStatusByYear);
      loading.set(year, true);
      set({ loadingStatusByYear: loading });

      try {
        const status = await getEngagementLetterStatus(businessId, year);

        const statusMap = new Map(get().statusByYear);
        statusMap.set(year, status);

        const loadingMap = new Map(get().loadingStatusByYear);
        loadingMap.set(year, false);

        set({ statusByYear: statusMap, loadingStatusByYear: loadingMap });
      } catch (error) {
        console.error(
          `[EngagementLetter] Failed to fetch status for year ${year}:`,
          error
        );
        const errors = new Map(get().statusErrors);
        errors.set(year, "Failed to fetch status");

        const loadingMap = new Map(get().loadingStatusByYear);
        loadingMap.set(year, false);

        set({ statusErrors: errors, loadingStatusByYear: loadingMap });
      }
    },

    // Check if engagement letter is signed for a year and show modal if needed
    checkAndShowModal: async (
      year: number,
      organizerStatus: string
    ): Promise<boolean> => {
      // Check if we already have status for this year
      // (Should already be loaded during organizer initialization)
      let status = get().statusByYear.get(year);
      const isLoading = get().loadingStatusByYear.get(year);

      // If not in cache and not already loading, fetch it (fallback for edge cases)
      if (!status && !isLoading) {
        await get().fetchStatusForYear(year);
        status = get().statusByYear.get(year);
      }
      // If already loading, wait for it to complete
      else if (!status && isLoading) {
        // Poll until loading is complete
        while (get().loadingStatusByYear.get(year)) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        status = get().statusByYear.get(year);
      }

      // PRIMARY CHECK: If already signed, never show modal regardless of organizer status
      if (status && status.status === "SIGNED") {
        return true; // Allow navigation
      }

      // SECONDARY CHECK: If organizer is already started/completed, don't block
      // (this handles edge cases where they bypassed the engagement letter somehow)
      if (
        organizerStatus !== "NOT_STARTED" &&
        organizerStatus !== "not started"
      ) {
        return true; // Allow navigation
      }

      // Not signed and organizer not started - show modal
      get().openModal(year);
      return false; // Block navigation
    },

    // Open modal for a specific year
    openModal: (year: number) => {
      set({ isModalOpen: true, currentYear: year });
      // Fetch template if not already loaded
      if (!get().template) {
        get().fetchTemplate();
      }
    },

    // Close modal
    closeModal: () => {
      set({ isModalOpen: false, currentYear: null });
    },

    // Sign the engagement letter
    signLetter: async (signature: string) => {
      const { template, currentYear } = get();
      if (!template) {
        throw new Error("Template not set");
      }
      if (!currentYear) {
        throw new Error("Tax year not set - cannot sign engagement letter");
      }

      try {
        await signEngagementLetter(template.id, signature, currentYear);

        // Update status for this year
        await get().fetchStatusForYear(currentYear);

        // Close modal
        get().closeModal();
      } catch (error) {
        throw error;
      }
    },

    // Update engagement letter settings (for tax pro)
    updateSettings: (title: string, content: string) => {
      set({
        engagementLetterSettings: {
          title,
          content,
          loading: false,
        },
      });
    },

    // Save template (for tax pro)
    saveTemplate: async () => {
      const { engagementLetterSettings } = get();

      set({
        engagementLetterSettings: {
          ...engagementLetterSettings,
          loading: true,
        },
      });

      try {
        await createEngagementLetterTemplate(
          engagementLetterSettings.title,
          engagementLetterSettings.content
        );

        // Refresh template and attachments
        await get().fetchTemplate();
        await get().fetchAttachments();

        set({
          isPublishModalOpen: false, // Close the modal
          engagementLetterSettings: {
            ...engagementLetterSettings,
            loading: false,
          },
        });
      } catch (error) {
        set({
          engagementLetterSettings: {
            ...engagementLetterSettings,
            loading: false,
          },
        });
        throw error;
      }
    },

    // Clear status for a specific year (useful for testing or refetching)
    clearStatusForYear: (year: number) => {
      const statusMap = new Map(get().statusByYear);
      statusMap.delete(year);
      set({ statusByYear: statusMap });
    },

    // Tax pro modal actions
    openPreviewModal: () => {
      set({ isPreviewModalOpen: true });
    },

    closePreviewModal: () => {
      set({ isPreviewModalOpen: false });
    },

    openPublishModal: () => {
      set({ isPublishModalOpen: true });
    },

    closePublishModal: () => {
      set({ isPublishModalOpen: false });
    },

    // Fetch preview PDF
    fetchPreviewPdf: async () => {
      const { template, engagementLetterSettings } = get();
      if (!template) {
        set({ previewError: "No template loaded" });
        return;
      }

      set({ isLoadingPreview: true, previewError: null, previewPdfUrl: null });
      try {
        // Send current local edits to preview endpoint
        const { presignedUrl } = await getPreviewPdf(template.id, {
          header: engagementLetterSettings.title,
          body: engagementLetterSettings.content,
        });
        console.log("[fetchPreviewPdf] Received presigned URL:", presignedUrl);
        console.log(
          "[fetchPreviewPdf] Sent header:",
          engagementLetterSettings.title
        );
        console.log(
          "[fetchPreviewPdf] Sent body length:",
          engagementLetterSettings.content?.length || 0
        );
        set({
          previewPdfUrl: presignedUrl,
          isLoadingPreview: false,
        });
      } catch (error) {
        set({
          previewError: "Failed to generate preview",
          isLoadingPreview: false,
        });
        console.error("Error fetching preview PDF:", error);
      }
    },

    // Fetch attachments for the current template
    fetchAttachments: async () => {
      const { template } = get();
      if (!template) {
        set({ attachmentError: "No template loaded" });
        return;
      }

      set({ isLoadingAttachments: true, attachmentError: null });
      try {
        const attachments = await listAttachments(template.id);
        set({
          attachments,
          isLoadingAttachments: false,
        });
      } catch (error) {
        set({
          attachmentError: "Failed to load attachments",
          isLoadingAttachments: false,
        });
      }
    },

    // Upload an attachment file
    uploadAttachmentFile: async (file: File) => {
      const { template } = get();
      if (!template) {
        throw new Error("No template loaded");
      }

      set({ isUploadingAttachment: true, attachmentError: null });
      try {
        const newAttachment = await uploadAttachment(template.id, file);

        // Add to attachments list
        const currentAttachments = get().attachments;
        const updatedAttachments = [...currentAttachments, newAttachment];

        set({
          attachments: updatedAttachments,
          isUploadingAttachment: false,
        });
      } catch (error) {
        set({
          attachmentError: "Failed to upload attachment",
          isUploadingAttachment: false,
        });
        throw error;
      }
    },

    // Delete an attachment by ID
    deleteAttachmentById: async (attachmentId: string) => {
      const { template, attachments } = get();
      if (!template) {
        throw new Error("No template loaded");
      }

      try {
        await deleteAttachment(template.id, attachmentId);

        // Remove from attachments list
        const updatedAttachments = attachments.filter(
          (a) => a.id !== attachmentId
        );

        set({
          attachments: updatedAttachments,
        });
      } catch (error) {
        set({
          attachmentError: "Failed to delete attachment",
        });
        throw error;
      }
    },

    // Reorder attachments
    reorderAttachments: async (attachmentIds: string[]) => {
      const { template, attachments } = get();
      if (!template) {
        throw new Error("No template loaded");
      }

      // Optimistically update UI
      const reorderedAttachments = attachmentIds
        .map((id) => attachments.find((a) => a.id === id))
        .filter((a): a is EngagementLetterAttachment => a !== undefined);

      set({
        attachments: reorderedAttachments,
      });

      try {
        await reorderAttachmentsAPI(template.id, attachmentIds);
      } catch (error) {
        // Revert on error
        set({ attachments });
        set({
          attachmentError: "Failed to reorder attachments",
        });
        throw error;
      }
    },
  })
);
